/**
 * The prototype service (UI/UX's, Figma's): the board's screens as a prototype (`model.ts`),
 * the document the Prototype window plays (`html.ts`, with the pictures read once each), the
 * screen it starts from, and the exported site: one HTML file that plays by itself.
 *
 * Nothing here is saved: where a click leads, which screen starts, a screen's device are the
 * board's own data (its items' and frames' `style`), so they are undone, shared and merged like
 * everything else.
 */
import { createEffect, createMemo, createSignal, type Accessor } from "solid-js";
import type { GraphMirror } from "../../../client/graph";
import { toBase64 } from "../../../shared/base64";
import { downloadText } from "../../../shared/download";
import type { AssetsService } from "../../../state/assets";
import type { KindsService } from "../../../state/kinds";
import { prototypeHtml } from "./html";
import { picturesOf, prototypeOf, type Prototype } from "./model";

export * from "./html";
export * from "./player";
export * from "./model";

/** What the service is built from. */
export interface PrototypeDeps {
  graph: Pick<GraphMirror, "state">;
  assets: Pick<AssetsService, "read" | "info">;
  /** Whether UI/UX is worked in (the pictures are read only then). */
  active: Accessor<boolean>;
  /** What each kind is on the board. */
  kinds: Pick<KindsService, "board">;
  /** The project's name (the exported file's). */
  title: Accessor<string>;
}

/** The prototype of the board worked in. */
export interface PrototypeService {
  /** The screens and where clicks lead. */
  model: Accessor<Prototype>;
  /** The document the Prototype window plays (no script in it). */
  html: Accessor<string>;
  /** The screen the window starts from (`null`: the prototype's own start). */
  start: Accessor<string | null>;
  setStart(id: string | null): void;
  /** Asks the window to play again from the start (it counts the requests). */
  restarts: Accessor<number>;
  restart(): void;
  /** Saves the prototype as one HTML file that plays by itself; `false` when it has no screen. */
  exportSite(): Promise<boolean>;
}

/** Starts the prototype service. */
export function createPrototype({ graph, assets, active, kinds, title }: PrototypeDeps): PrototypeService {
  const model = createMemo(() => prototypeOf(graph.state.graph, kinds.board));
  /** Pictures read: project file → `data:` address (by the file's last change, read again when it changes). */
  const [pictures, setPictures] = createSignal<ReadonlyMap<string, string>>(new Map());
  const asked = new Map<string, string>();
  const [start, setStart] = createSignal<string | null>(null);
  const [restarts, setRestarts] = createSignal(0);

  /** The `data:` address of a project file, read once per change. */
  const read = async (path: string): Promise<string | null> => {
    const content = await assets.read("project", path, true);
    if (!content || !content.info.mime.startsWith("image/")) return null;
    return `data:${content.info.mime};base64,${toBase64(content.bytes)}`;
  };
  createEffect(() => {
    if (!active()) return;
    for (const path of picturesOf(model())) {
      const stamp = `${assets.info(path)?.modified_ms ?? 0}`;
      if (asked.get(path) === stamp) continue;
      asked.set(path, stamp);
      void read(path).then((url) => {
        if (!url) return;
        const next = new Map(pictures());
        next.set(path, url);
        setPictures(next);
      });
    }
  });

  const html = createMemo(() => prototypeHtml(model(), { title: title() || "Prototype", pictures: pictures(), start: start() }));

  return {
    model,
    html,
    start: () => (start() && model().screens.some((s) => s.id === start()) ? start() : null),
    setStart,
    restarts,
    restart: () => setRestarts(restarts() + 1),
    async exportSite() {
      const proto = model();
      if (!proto.screens.length) return false;
      // Every picture, even one still being read for the window.
      const all = new Map(pictures());
      for (const path of picturesOf(proto)) if (!all.has(path)) all.set(path, (await read(path)) ?? "");
      const name = (title() || "Prototype").replace(/[\\/:*?"<>|]+/g, " ").trim() || "Prototype";
      downloadText(`${name} prototype.html`, prototypeHtml(proto, { title: `${name}: prototype`, pictures: all, site: true }), "text/html");
      return true;
    },
  };
}
